import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';
import axios from 'axios';
import { toast } from 'sonner';
import { Share2, Users, Gift, Copy, Check, MessageCircle, Award, X, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useIsDesktop } from '../hooks/useMediaQuery';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReferralPanel = ({ refreshKey = 0 }) => {
  const isDesktop = useIsDesktop();
  const [expanded, setExpanded] = useState(isDesktop);
  const [referralInfo, setReferralInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [redeemModal, setRedeemModal] = useState(null);
  const [redeeming, setRedeeming] = useState(false);
  const [pops, setPops] = useState({}); // ref_id -> { delta, from } for the +N bubble animation
  const [unlocked, setUnlocked] = useState({}); // ref_id -> true for the reward-unlock glow
  const prevDistRef = useRef({});

  const token = localStorage.getItem('token');

  const fetchReferralInfo = async () => {
    try {
      const response = await axios.get(`${API}/user/referral-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReferralInfo(response.data);
    } catch (error) {
      console.error('Error fetching referral info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferralInfo();
  }, [token, refreshKey]);

  // Light polling so a bubble fills (with a +1 pop) in near real-time when a ticket is issued.
  useEffect(() => {
    const id = setInterval(fetchReferralInfo, 12000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [token]);

  // Detect increases in each friend's bubble count → +N pop; and reward unlock (reaching 5) → confetti.
  useEffect(() => {
    const friends = referralInfo?.referred_users;
    if (!friends) return;
    const next = {};
    const newPops = {};
    const justUnlocked = [];
    friends.forEach((f) => {
      const cur = f.distributions || 0;
      const prev = prevDistRef.current[f.ref_id];
      if (prev !== undefined && cur > prev) newPops[f.ref_id] = { delta: cur - prev, from: prev };
      if (prev !== undefined && prev < 5 && cur >= 5) justUnlocked.push(f.ref_id);
      next[f.ref_id] = cur;
    });
    prevDistRef.current = next;

    const timers = [];
    if (Object.keys(newPops).length) {
      setPops(newPops);
      timers.push(setTimeout(() => setPops({}), 1500));
    }
    if (justUnlocked.length) {
      fireRewardConfetti();
      setUnlocked((u) => ({ ...u, ...Object.fromEntries(justUnlocked.map((id) => [id, true])) }));
      timers.push(setTimeout(() => setUnlocked({}), 2500));
    }
    return () => timers.forEach(clearTimeout);
  }, [referralInfo]);

  const fireRewardConfetti = () => {
    const colors = ['#22c55e', '#EAB308', '#A855F7', '#ffffff'];
    confetti({ particleCount: 90, spread: 75, startVelocity: 42, origin: { x: 0.5, y: 0.65 }, colors, zIndex: 9999, scalar: 0.9 });
    setTimeout(() => confetti({ particleCount: 50, spread: 100, startVelocity: 30, origin: { x: 0.5, y: 0.6 }, colors, zIndex: 9999, scalar: 0.7 }), 180);
  };

  const referralLink = referralInfo?.referral_code 
    ? `${window.location.origin}?ref=${referralInfo.referral_code}`
    : '';

  const whatsappMessage = encodeURIComponent(
    `Join RafflesRUs and get a chance to win amazing prizes!\n\nUse my link to sign up: ${referralLink}`
  );

  const whatsappUrl = `https://wa.me/?text=${whatsappMessage}`;

  const copyLink = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRedeem = async (friend) => {
    setRedeeming(true);
    try {
      await axios.post(
        `${API}/user/redeem-referral/${friend.ref_id}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRedeemModal({ ...friend, justRedeemed: true });
      fetchReferralInfo();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to redeem');
    } finally {
      setRedeeming(false);
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse rounded-2xl border border-white/[0.06] bg-[#18181b] p-5">
        <div className="mb-3 h-5 w-1/3 rounded bg-white/[0.06]"></div>
        <div className="h-14 rounded bg-white/[0.06]"></div>
      </div>
    );
  }

  const friends = referralInfo?.global_friends_count || 0;
  const bonus = referralInfo?.global_bonus || 0;
  const readyToRedeem = referralInfo?.referred_users?.filter((f) => (f.distributions || 0) >= 5 && !f.redeemed).length || 0;

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="overflow-hidden rounded-2xl border border-white/[0.06] bg-[#18181b]"
        data-testid="referral-panel"
      >
        {/* Compact header — always visible, toggles details */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          data-testid="referral-toggle-btn"
          className="tap-none flex w-full items-center gap-3 px-4 py-4 text-left active:bg-white/[0.03] sm:px-5"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#10b981]/15">
            <Share2 className="h-5 w-5 text-[#10b981]" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-heading text-[15px] font-semibold text-white">Invite friends</h3>
            <p className="truncate text-xs text-[#a1a1aa]">
              <span className="font-semibold text-[#39FF14]">{friends}</span> invited · <span className="font-semibold text-[#39FF14]">£{bonus}</span> added to every prize pot
            </p>
          </div>
          {readyToRedeem > 0 && (
            <span className="shrink-0 animate-pulse rounded-full bg-[#22c55e] px-2 py-0.5 text-[10px] font-bold text-black">
              {readyToRedeem} reward{readyToRedeem > 1 ? 's' : ''}
            </span>
          )}
          <ChevronDown className={`h-5 w-5 shrink-0 text-[#52525b] transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
        </button>

        <AnimatePresence initial={false}>
          {expanded && (
            <motion.div
              key="details"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.22, ease: 'easeOut' }}
              className="overflow-hidden"
            >
        <div className="border-t border-white/[0.06] px-4 pb-5 pt-4 sm:px-5">
        {/* Stats */}
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-[#09090b]/60 p-3.5">
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Users className="h-4 w-4 text-[#A855F7]" />
              <span className="text-[11px] font-medium uppercase tracking-wider">Invited</span>
            </div>
            <div className="mt-1 font-heading text-2xl font-semibold tabular" style={{ color: '#39FF14', textShadow: friends > 0 ? '0 0 10px rgba(57,255,20,0.5)' : 'none' }}>
              {friends}
            </div>
          </div>
          <div className="rounded-xl bg-[#09090b]/60 p-3.5">
            <div className="flex items-center gap-2 text-[#a1a1aa]">
              <Gift className="h-4 w-4 text-[#10b981]" />
              <span className="text-[11px] font-medium uppercase tracking-wider">To prizes</span>
            </div>
            <div className="mt-1 font-heading text-2xl font-semibold tabular" style={{ color: '#39FF14', textShadow: bonus > 0 ? '0 0 10px rgba(57,255,20,0.5)' : 'none' }}>
              £{bonus}
            </div>
          </div>
        </div>

        <div className="mb-4 rounded-xl border border-[#EAB308]/25 bg-[#EAB308]/10 px-3.5 py-2.5">
          <p className="text-sm text-[#EAB308]">
            Every friend who joins adds <span className="font-bold">£5</span> to all prize pots.
          </p>
        </div>

        {/* Referred Friends Progress */}
        <div className="space-y-2 mb-4">
          <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[#71717a]">Your referrals</h4>
          {referralInfo?.referred_users?.length > 0 ? (
            referralInfo.referred_users.map((friend, idx) => {
              const filled = friend.distributions || 0;
              const canRedeem = filled >= 5 && !friend.redeemed;
              const isRedeemed = friend.redeemed;
              const isUnlocking = unlocked[friend.ref_id];

              return (
                <motion.div
                  key={idx}
                  animate={isUnlocking ? { boxShadow: ['0 0 0px rgba(34,197,94,0)', '0 0 22px rgba(34,197,94,0.7)', '0 0 0px rgba(34,197,94,0)'] } : {}}
                  transition={{ duration: 1.2, repeat: isUnlocking ? 1 : 0 }}
                  className={`rounded-xl bg-[#09090b]/60 px-3.5 py-3 flex items-center justify-between gap-3 ${
                    canRedeem ? 'ring-1 ring-[#22c55e]/50' : ''
                  }`}
                  data-testid={`referral-friend-${idx}`}
                >
                  <span className="text-white text-sm font-medium truncate min-w-0">{friend.name}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className="relative flex items-center gap-1.5">
                      {[...Array(5)].map((_, i) => {
                        const isFilled = i < filled;
                        const pop = pops[friend.ref_id];
                        const isNewlyFilled = pop && i >= pop.from && i < filled;
                        return (
                          <motion.div
                            key={i}
                            animate={isNewlyFilled ? { scale: [0.3, 1.4, 1] } : { scale: 1 }}
                            transition={{ duration: 0.5, ease: 'easeOut' }}
                            className={`w-3 h-3 rounded-full ${
                              isFilled
                                ? 'bg-[#22c55e] shadow-[0_0_6px_rgba(34,197,94,0.5)]'
                                : 'bg-[#3f3f46]'
                            }`}
                            data-testid={`bubble-${idx}-${i}`}
                          />
                        );
                      })}
                      <AnimatePresence>
                        {pops[friend.ref_id] && (
                          <motion.span
                            key="pop"
                            initial={{ opacity: 0, y: 4, scale: 0.5 }}
                            animate={{ opacity: 1, y: -16, scale: 1 }}
                            exit={{ opacity: 0, y: -26 }}
                            transition={{ duration: 0.6, ease: 'easeOut' }}
                            className="pointer-events-none absolute -top-2 right-0 text-xs font-extrabold text-[#22c55e]"
                            style={{ textShadow: '0 0 8px rgba(34,197,94,0.7)' }}
                            data-testid={`bubble-pop-${idx}`}
                          >
                            +{pops[friend.ref_id].delta}
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </div>
                    {canRedeem && (
                      <button
                        onClick={() => setRedeemModal(friend)}
                        className="ml-1 px-2.5 py-1 bg-[#22c55e] text-white text-xs font-bold rounded-full animate-pulse hover:bg-[#16a34a] transition-colors"
                        data-testid={`redeem-btn-${idx}`}
                      >
                        Redeem
                      </button>
                    )}
                    {isRedeemed && (
                      <span className="ml-1 px-2 py-1 bg-[#3f3f46] text-[#a1a1aa] text-xs rounded-full">
                        Redeemed
                      </span>
                    )}
                  </div>
                </motion.div>
              );
            })
          ) : (
            <div className="rounded-xl bg-[#09090b]/60 px-4 py-4 text-center">
              <p className="text-[#a1a1aa] text-sm">Refer a friend to see their progress here</p>
            </div>
          )}
        </div>

        {/* Share Buttons */}
        <div className="space-y-2.5">
          <a
            href={whatsappUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="whatsapp-share-btn"
            className="tap-none flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] font-semibold text-black transition-colors hover:bg-[#20bd5a] active:scale-[0.98]"
          >
            <MessageCircle className="w-5 h-5" />
            Share via WhatsApp
          </a>

          <div className="flex gap-2">
            <div className="flex-1 min-w-0 truncate rounded-full border border-white/[0.08] bg-[#09090b] px-4 py-3 text-sm text-[#a1a1aa]">
              {referralLink}
            </div>
            <Button
              onClick={copyLink}
              data-testid="copy-referral-link-btn"
              variant="outline"
              className={`h-12 w-12 shrink-0 rounded-full p-0 ${copied ? 'border-[#10b981] text-[#10b981]' : 'border-white/[0.08] text-[#a1a1aa]'} hover:text-white`}
            >
              {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        <div className="mt-4 text-center">
          <span className="text-xs text-[#52525b]">Your referral code: </span>
          <span className="text-sm font-mono text-[#EAB308] font-bold">{referralInfo?.referral_code}</span>
        </div>
        </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Redeem Modal */}
      <AnimatePresence>
        {redeemModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
            onClick={() => setRedeemModal(null)}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-[#18181b] border border-white/[0.08] rounded-3xl p-6 sm:p-8 max-w-sm w-full text-center"
            >
              <button
                onClick={() => setRedeemModal(null)}
                className="absolute top-4 right-4 text-[#52525b] hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="bg-[#22c55e]/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-[#22c55e]" />
              </div>

              {redeemModal.justRedeemed || redeemModal.redeemed ? (
                <>
                  <h3 className="text-2xl font-bold text-white mb-2">Reward Redeemed!</h3>
                  <p className="text-[#a1a1aa] text-sm mb-4">
                    For referring <span className="text-[#22c55e] font-medium">{redeemModal.name}</span>
                  </p>
                  <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl p-4 mb-4">
                    <p className="text-[#22c55e] font-medium text-lg">
                      {referralInfo?.referral_prize_text || 'Show this to the admin to claim your referral reward!'}
                    </p>
                  </div>
                  <p className="text-xs text-[#52525b]">Show this screen to the admin</p>
                </>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-white mb-2">Claim Your Reward!</h3>
                  <p className="text-[#a1a1aa] text-sm mb-4">
                    <span className="text-[#22c55e] font-medium">{redeemModal.name}</span> has received 5 ticket distributions!
                  </p>
                  <div className="bg-[#22c55e]/10 border border-[#22c55e]/30 rounded-xl p-4 mb-6">
                    <p className="text-[#22c55e] font-medium">
                      {referralInfo?.referral_prize_text || 'Show this to the admin to claim your referral reward!'}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleRedeem(redeemModal)}
                    disabled={redeeming}
                    data-testid="confirm-redeem-btn"
                    className="w-full bg-[#22c55e] hover:bg-[#16a34a] text-white font-bold rounded-full h-12"
                  >
                    {redeeming ? 'Redeeming...' : 'Redeem Now'}
                  </Button>
                </>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ReferralPanel;
